import { getOrCreateSessionId } from "./session";

declare const process: { env: Record<string, string | undefined> };

type ClarityFn = (...args: unknown[]) => void;

interface ClarityWindow extends Window {
	clarity?: ClarityFn;
	PrimeStyleAIClarityProjectId?: string;
	PRIMESTYLE_CLARITY_PROJECT_ID?: string;
}

interface SdkClarityContext {
	projectId?: string | null;
	sdkOpenId?: string | null;
	productId?: string | null;
	productTitle?: string | null;
	productUrl?: string | null;
	view?: string | null;
	jobId?: string | null;
	source?: string | null;
}

const SCRIPT_ID_PREFIX = "ps-clarity-script";
const DEFAULT_PROJECT_ID = "wwb75cfm2z";
let initializedProjectId: string | null = null;

function cleanProjectId(value?: string | null): string | null {
	const trimmed = String(value || "").trim();
	if (!trimmed) return null;
	// Clarity project ids are public, short alphanumeric ids.
	return /^[a-z0-9]+$/i.test(trimmed) ? trimmed : null;
}

function readEnvProjectId(): string | null {
	let id = "";
	try {
		id = process.env.NEXT_PUBLIC_PRIMESTYLE_CLARITY_PROJECT_ID ?? "";
	} catch {
		// Pure browser host with no process shim.
	}
	if (!id) {
		try {
			id = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID ?? "";
		} catch {
			// Pure browser host with no process shim.
		}
	}
	return cleanProjectId(id);
}

function readWindowProjectId(): string | null {
	if (typeof window === "undefined") return null;
	const w = window as ClarityWindow;
	return cleanProjectId(w.PrimeStyleAIClarityProjectId || w.PRIMESTYLE_CLARITY_PROJECT_ID);
}

export function resolveSdkClarityProjectId(projectId?: string | null): string | null {
	return cleanProjectId(projectId) || readWindowProjectId() || readEnvProjectId() || DEFAULT_PROJECT_ID;
}

function getClarity(): ClarityFn | null {
	if (typeof window === "undefined") return null;
	const clarity = (window as ClarityWindow).clarity;
	return typeof clarity === "function" ? clarity : null;
}

function installClarity(projectId: string): ClarityFn | null {
	if (typeof window === "undefined" || typeof document === "undefined") return null;
	const scriptId = `${SCRIPT_ID_PREFIX}-${projectId}`;
	if (!document.getElementById(scriptId)) {
		((c: ClarityWindow & Record<string, unknown>, l: Document, a: string, r: string, i: string) => {
			c[a] = c[a] || function (...args: unknown[]) {
				const queued = c[a] as { q?: unknown[][] };
				(queued.q = queued.q || []).push(args);
			};
			const script = l.createElement(r) as HTMLScriptElement;
			const firstScript = l.getElementsByTagName(r)[0];
			script.id = scriptId;
			script.async = true;
			script.src = "https://www.clarity.ms/tag/" + i;
			firstScript?.parentNode?.insertBefore(script, firstScript);
		})(window as unknown as ClarityWindow & Record<string, unknown>, document, "clarity", "script", projectId);
	}
	return getClarity();
}

function setTag(key: string, value?: string | number | boolean | null): void {
	const clarity = getClarity();
	if (!clarity || value === undefined || value === null || value === "") return;
	try {
		clarity("set", key, String(value).slice(0, 512));
	} catch {
		// Analytics must never affect SDK UX.
	}
}

export function tagSdkClaritySession(input: SdkClarityContext = {}): void {
	const sessionId = getOrCreateSessionId();
	setTag("ps_session", sessionId);
	setTag("ps_sdk_session", sessionId);
	setTag("ps_sdk_open_id", input.sdkOpenId || undefined);
	setTag("ps_product_id", input.productId || undefined);
	setTag("ps_product_title", input.productTitle || undefined);
	setTag("ps_product_url", input.productUrl || undefined);
	setTag("ps_sdk_view", input.view || undefined);
	setTag("ps_tryon_job_id", input.jobId || undefined);
	setTag("ps_source", input.source || undefined);

	const clarity = getClarity();
	if (clarity && input.sdkOpenId) {
		try {
			clarity("identify", sessionId, input.sdkOpenId, input.productId || input.productUrl || undefined, input.productTitle || undefined);
		} catch {
			// Analytics must never affect SDK UX.
		}
	}
}

export function startSdkClarity(input: SdkClarityContext = {}): boolean {
	if (typeof window === "undefined" || typeof document === "undefined") return false;
	const projectId = resolveSdkClarityProjectId(input.projectId);
	if (projectId) {
		initializedProjectId = initializedProjectId || projectId;
		installClarity(initializedProjectId);
	}
	if (!getClarity()) return false;
	tagSdkClaritySession(input);
	return true;
}
